<template>
  <div class="courses-list">
    <UISection :title="computedTitle">
      <template #default>
        <div class="courses-grid" v-if="!loading && !error && courses.length > 0">
          <div v-for="course in courses" :key="course.id" class="course-card" @click="openCourseDetail(course.id)">
            <div class="course-image" v-if="course.photo">
              <img :src="course.photo" :alt="course.name" />
            </div>
            <div class="course-content">
              <h3>{{ course.name }}</h3>
              <div v-if="course.author" class="course-author">
                <a :href="`/teacher/${course.author.id}`" @click.stop class="author-link">
                  <img v-if="course.author.photo" :src="course.author.photo" :alt="course.author.name" class="author-photo" />
                  <span class="author-name">{{ course.author.name }}</span>
                </a>
              </div>
              <div class="course-footer">
                <div class="course-price">
                  <template v-if="!course.hasAccess">
                    <span v-if="course.is_free" class="price-free">Бесплатно</span>
                    <span v-else class="price-amount">{{ formatPrice(course.price) }} ₽</span>
                  </template>
                  <template v-else>
                    <div class="course-status">
                      <span v-if="course.isCompleted && course.progress === 100" class="enrolled-badge completed">
                        ✓ Завершен
                      </span>
                      <span v-else class="enrolled-badge">
                        ✓ Зачислен
                      </span>
                      <div v-if="course.progress !== undefined" class="progress-mini">
                        <div class="progress-mini-bar">
                          <div class="progress-mini-fill" :style="{ width: course.progress + '%' }" :class="{ completed: course.progress === 100 }"></div>
                        </div>
                        <span class="progress-mini-text">{{ course.progress }}%</span>
                      </div>
                    </div>
                  </template>
                </div>
                <button 
                  class="course-button" 
                  :class="getButtonClass(course)"
                  @click.stop="handleCourseAction(course)"
                >
                  {{ getButtonText(course) }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="!loading && !error && courses.length === 0" class="empty-state">
          <i class="fas fa-book"></i>
          <p>Пока нет доступных курсов</p>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>Загрузка курсов...</p>
        </div>

        <!-- Error state -->
        <div v-if="error" class="error-state">
          <p>{{ error }}</p>
          <button @click="fetchCourses" class="retry-button">Попробовать снова</button>
        </div>
      </template>
    </UISection>
  </div>
</template>

<script>
import UISection from './ui/UISection.vue';
import axios from 'axios';

export default {
  name: 'CoursesList',
  components: {
    UISection
  },
  props: {
    categoryId: {
      type: Number,
      default: null
    },
    categoryIds: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: null
    }
  },
  computed: {
    computedTitle() {
      if (this.title) {
        return this.title;
      }
      if (this.categoryNames && this.categoryNames.length > 0) {
        return this.categoryNames[0];
      }
      return 'Все курсы';
    }
  },
  data() {
    return {
      courses: [],
      loading: true,
      error: null,
      isLoggedIn: false,
      categoryNames: []
    };
  },
  watch: {
    categoryId: {
      immediate: true,
      handler() {
        this.fetchCourses();
      }
    },
    categoryIds: {
      immediate: true,
      handler() {
        this.fetchCourses();
      }
    }
  },
  mounted() {
    this.fetchCourses();
  },
  methods: {
    async fetchCourses() {
      this.loading = true;
      this.error = null;
      try {
        const params = {};
        
        // Поддержка множественных категорий
        if (this.categoryIds && this.categoryIds.length > 0) {
          params.category_ids = this.categoryIds.join(',');
          // Загружаем названия категорий для заголовка
          await this.loadCategoryNames(this.categoryIds);
        } else if (this.categoryId) {
          params.category_id = this.categoryId;
          // Загружаем название категории для заголовка
          await this.loadCategoryNames([this.categoryId]);
        } else {
          this.categoryNames = [];
        }
        
        const response = await axios.get('/api/v1/public/courses', { params });
        const coursesData = response.data;
        
        // Проверяем авторизацию и доступ к каждому курсу
        const token = localStorage.getItem('studentAccessToken');
        this.isLoggedIn = !!token;
        
        if (token) {
          const accessPromises = coursesData.map(async (course) => {
            try {
          const accessResponse = await axios.get(`/api/v1/school/products/${course.id}/access`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              return {
                ...course,
                hasAccess: accessResponse.data.has_access,
                isCompleted: accessResponse.data.is_completed || false,
                progress: accessResponse.data.progress_percent || 0
              };
            } catch (err) {
              console.error(`Ошибка проверки доступа к курсу ${course.id}:`, err);
              return { ...course, hasAccess: false, isCompleted: false, progress: 0 };
            }
          });
          
          this.courses = await Promise.all(accessPromises);
        } else {
          this.courses = coursesData.map(course => ({ ...course, hasAccess: false, isCompleted: false, progress: 0 }));
        }
      } catch (error) {
        console.error('Ошибка загрузки курсов:', error);
        this.error = 'Не удалось загрузить курсы. Пожалуйста, попробуйте позже.';
      } finally {
        this.loading = false;
      }
    },
    formatPrice(price) {
      if (!price) return '0';
      return new Intl.NumberFormat('ru-RU').format(price);
    },
    openCourseDetail(courseId) {
      // Всегда используем slug
      const course = this.courses.find(c => c.id === courseId);
      if (course && course.slug) {
        this.$router.push(`/product/${course.slug}`);
      } else {
        // Если slug нет, используем id как fallback (для старых продуктов)
        this.$router.push(`/product/${courseId}`);
      }
    },
    handleCourseAction(course) {
      const slug = course.slug || course.id;
      
      // Если не авторизован - переходим на страницу входа с редиректом
      if (!this.isLoggedIn) {
        window.location.href = `/school/login?redirect=/school/product/${slug}`;
        return;
      }
      
      // Если уже зачислен - переходим к обучению
      if (course.hasAccess) {
        window.location.href = `/school/product/${slug}/learn`;
        return;
      }
      
      // Если не зачислен - переходим на страницу продукта в личном кабинете
      window.location.href = `/school/product/${slug}`;
    },
    getButtonText(course) {
      if (!this.isLoggedIn) {
        return 'Войти';
      }
      if (!course.hasAccess) {
        return course.is_free ? 'Начать' : 'Купить';
      }
      if (course.progress === 100) {
        return 'Начать заново';
      }
      if (course.progress > 0) {
        return 'Продолжить';
      }
      return 'Начать';
    },
    getButtonClass(course) {
      if (!course.hasAccess) {
        return course.is_free ? 'outline' : 'primary';
      }
      if (course.progress === 100) {
        return 'completed';
      }
      return 'enrolled';
    },
    async loadCategoryNames(categoryIds) {
      try {
        // Сначала пробуем получить из видимых категорий
        const response = await axios.get('/api/v1/public/product-categories');
        const allCategories = response.data;
        
        const names = [];
        const missingIds = [];
        
        categoryIds.forEach(id => {
          const category = allCategories.find(cat => cat.id === id);
          if (category) {
            names.push(category.name);
          } else {
            missingIds.push(id);
          }
        });
        
        // Если не все категории найдены в видимых, получаем их по ID без проверки видимости
        if (missingIds.length > 0) {
          try {
            const idsResponse = await axios.get(`/api/v1/public/product-categories/by-ids/${missingIds.join(',')}`);
            const hiddenCategories = idsResponse.data;
            hiddenCategories.forEach(cat => {
              names.push(cat.name);
            });
          } catch (error) {
            console.error('Ошибка загрузки скрытых категорий:', error);
          }
        }
        
        this.categoryNames = names;
      } catch (error) {
        console.error('Ошибка загрузки категорий:', error);
        this.categoryNames = [];
      }
    }
  }
};
</script>

<style scoped>
.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 32px;
  padding: 20px 0;
}

.course-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.course-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.course-image {
  width: 100%;
  height: 240px;
  overflow: hidden;
  background: #f3f4f6;
}

.course-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.course-card:hover .course-image img {
  transform: scale(1.05);
}

.course-content {
  padding: 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.course-content h3 {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
  flex: 1;
}

.course-author {
  margin-bottom: 12px;
}

.author-link {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: #6b7280;
  transition: color 0.2s ease;
}

.author-link:hover {
  color: #e63946;
}

.author-photo {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.author-name {
  font-size: 0.9rem;
  font-weight: 500;
}

.course-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.course-price {
  font-size: 24px;
  font-weight: 700;
}

.price-amount {
  color: #e63946;
}

.price-free {
  color: #10b981;
}

.course-status {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.enrolled-badge {
  padding: 6px 14px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.9rem;
  background: #dcfce7;
  color: #16a34a;
  display: inline-block;
  width: fit-content;
}

.enrolled-badge.completed {
  background: #dbeafe;
  color: #1e40af;
}

.progress-mini {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-mini-bar {
  flex: 1;
  height: 6px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.progress-mini-fill {
  height: 100%;
  background: linear-gradient(90deg, #e63946 0%, #d62839 100%);
  border-radius: 999px;
  transition: width 0.3s ease;
}

.progress-mini-fill.completed {
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
}

.progress-mini-text {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  min-width: 32px;
}

.course-button {
  padding: 10px 20px;
  border: 2px solid #e63946;
  background: transparent;
  color: #e63946;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.course-button:hover {
  background: #e63946;
  color: white;
}

.course-button.primary {
  background: #e63946;
  color: white;
}

.course-button.primary:hover {
  background: #d62839;
}

.course-button.enrolled {
  background: #16a34a;
  border-color: #16a34a;
  color: white;
}

.course-button.enrolled:hover {
  background: #15803d;
  border-color: #15803d;
}

.course-button.completed {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

.course-button.completed:hover {
  background: #2563eb;
  border-color: #2563eb;
}

/* States */
.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f4f6;
  border-top-color: #e63946;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state i {
  font-size: 64px;
  color: #d1d5db;
  margin-bottom: 16px;
}

.error-state p {
  color: #ef4444;
  margin-bottom: 16px;
}

.retry-button {
  padding: 12px 24px;
  background: #e63946;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: background 0.3s ease;
}

.retry-button:hover {
  background: #d62839;
}

/* Responsive */
@media (max-width: 768px) {
  .courses-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
}
</style>

